import { useMemo, useState } from "react";
import { Plus, TriangleAlert, X } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/Modals";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ALL_CATEGORIES, filterMenuItems, menuCategories } from "@/lib/menu";

/**
 * The bootstrap menu keys every item by the server's `menu_items.id` as a string. The
 * demo seed the app falls back to before it has synced uses "m1"-style ids, which
 * /api/combos cannot resolve — offering those would guarantee a 404 on save.
 */
const isServerItem = item => /^\d+$/.test(String(item.id));

const round2 = value => Math.round(value * 100) / 100;

/**
 * Create a combo, or replace the components of one that exists.
 *
 * A combo is an ordinary menu_items row flagged is_combo, priced as a single line,
 * with combo_components naming what is in it. The API mirrors that split, and so does
 * this dialog: POST /combos builds the whole thing, and afterwards only
 * PUT /combos/{id}/components can change it. Name, price and availability are then
 * the menu item's own fields, edited from the menu catalogue — there is no combos
 * endpoint for them, so this dialog shows them read-only rather than offering inputs
 * that would be silently discarded.
 */
export default function ComboDialog({ combo, menu, categories, excludeIds, onCreate, onReplace, onClose }) {
  const editing = Boolean(combo?.id);
  const [form, setForm] = useState({
    name: combo?.name || "",
    short_name: combo?.short_name || "",
    price: String(combo?.price ?? ""),
    category_id: combo?.category_id ?? categories?.[0]?.id,
    is_veg: combo ? Boolean(combo.is_veg) : true,
  });
  const [lines, setLines] = useState(() =>
    (combo?.components || []).map(component => ({
      menu_item_id: component.menu_item_id,
      name: component.name,
      price: Number(component.price) || 0,
      quantity: component.quantity,
    }))
  );
  const [category, setCategory] = useState(ALL_CATEGORIES);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const set = (key, value) => setForm(current => ({ ...current, [key]: value }));

  // A combo may not contain another combo — the server rejects it because there is no
  // defined KOT behaviour for a nested one. Keep them out of the picker so the
  // operator never builds a list that cannot be saved.
  const pickable = useMemo(
    () => (menu || []).filter(item => isServerItem(item) && !excludeIds?.has(String(item.id))),
    [menu, excludeIds]
  );
  const tabs = useMemo(() => menuCategories(pickable), [pickable]);
  const results = useMemo(
    () => filterMenuItems(pickable, category, search),
    [pickable, category, search]
  );

  const add = item =>
    setLines(current => {
      const id = Number(item.id);
      // The server merges repeated components by summing quantity. Do the same here so
      // the parts total on screen is the one that ends up stored.
      if (current.some(line => line.menu_item_id === id)) {
        return current.map(line =>
          line.menu_item_id === id ? { ...line, quantity: line.quantity + 1 } : line
        );
      }
      return [...current, { menu_item_id: id, name: item.name, price: Number(item.price) || 0, quantity: 1 }];
    });

  const setQuantity = (id, value) =>
    setLines(current =>
      current.map(line =>
        // Below 1 is a 400 from the service, so it never leaves the screen.
        line.menu_item_id === id ? { ...line, quantity: Math.max(1, Math.round(Number(value) || 1)) } : line
      )
    );

  const remove = id => setLines(current => current.filter(line => line.menu_item_id !== id));

  const partsTotal = round2(lines.reduce((sum, line) => sum + line.price * line.quantity, 0));
  const price = Number(form.price);
  const priced = Number.isFinite(price) && form.price !== "";
  // components_total - price, exactly as combo_service computes it. Negative means the
  // combo costs more than the parts, which is a pricing mistake worth showing.
  const savings = priced ? round2(partsTotal - price) : null;

  const components = () =>
    lines.map(line => ({ menu_item_id: line.menu_item_id, quantity: line.quantity }));

  const submit = async () => {
    // The service answers 400 for an empty component list; catching it here saves a
    // round trip rather than substituting for the server's check.
    if (!lines.length) return toast.error("A combo needs at least one item");
    if (!editing) {
      if (!form.name.trim()) return toast.error("Name is required");
      if (!priced || price < 0) return toast.error("Price must be zero or more");
      if (!form.category_id) return toast.error("Pick a category");
    }

    setSaving(true);
    const result = editing
      ? await onReplace(combo.id, components())
      : await onCreate({
          name: form.name.trim(),
          short_name: form.short_name.trim() || null,
          category_id: Number(form.category_id),
          // Rupees, undivided — the same number the menu and the bill print.
          price,
          is_veg: form.is_veg,
          // tax_percent is omitted deliberately: the server inherits the outlet default.
          components: components(),
        });
    setSaving(false);
    // Nothing is announced before the server has answered.
    if (!result.ok) return toast.error(result.error);
    toast.success(editing ? `${combo.name} components saved` : `${form.name.trim()} created`);
    onClose();
  };

  return <Modal title={editing ? `Edit ${combo.name}` : "New combo"} onClose={onClose} testId="combo-dialog">
    <div className="workflow-form">
      {editing ? (
        <p className="hint" data-testid="combo-fixed-fields">
          {combo.name} · ₹{combo.price} · {combo.is_available ? "on the menu" : "off the menu"}.
          The combos API only replaces components. Change the name, the price or availability
          from the menu catalogue, where the combo appears as an ordinary item.
        </p>
      ) : (
        <>
          <label>Name<Input value={form.name} onChange={e => set("name", e.target.value)} data-testid="combo-name-input"/></label>
          <label>Short code<Input value={form.short_name} onChange={e => set("short_name", e.target.value)} placeholder="FMC" data-testid="combo-code-input"/></label>
          <label>Category
            <select value={form.category_id ?? ""} onChange={e => set("category_id", e.target.value)} data-testid="combo-category-select">
              {(categories || []).map(item => <option value={item.id} key={item.id}>{item.name}</option>)}
            </select>
          </label>
          <label>Combo price (₹)<Input type="number" min="0" step="0.01" value={form.price} onChange={e => set("price", e.target.value)} data-testid="combo-price-input"/></label>
          <label className="toggle-row">Vegetarian
            <input type="checkbox" checked={form.is_veg} onChange={e => set("is_veg", e.target.checked)} data-testid="combo-veg-checkbox"/>
          </label>
          <p className="hint">A new combo goes on the menu straight away. Take it off with Deactivate in the list.</p>
        </>
      )}

      <div data-testid="combo-component-list">
        <strong>What is in it</strong>
        {lines.map(line => (
          <div className="combo-pick-row" key={line.menu_item_id} data-testid={`combo-component-${line.menu_item_id}`}>
            <span>{line.name} <small>₹{line.price}</small></span>
            <Input type="number" min="1" step="1" value={line.quantity} onChange={e => setQuantity(line.menu_item_id, e.target.value)} data-testid={`combo-component-${line.menu_item_id}-quantity-input`}/>
            <button onClick={() => remove(line.menu_item_id)} aria-label={`Remove ${line.name}`} data-testid={`combo-component-${line.menu_item_id}-remove-button`}><X/></button>
          </div>
        ))}
        {!lines.length && <p className="chart-empty" data-testid="combo-components-empty">Nothing added yet.</p>}
      </div>

      <div className="combo-preview" data-testid="combo-savings-preview">
        <span>Parts total <strong data-testid="combo-parts-total">₹{partsTotal}</strong></span>
        {savings === null ? (
          <i data-testid="combo-savings-pending">Enter a price to see the saving</i>
        ) : savings >= 0 ? (
          <i data-testid="combo-savings">saves ₹{savings}</i>
        ) : (
          <i className="negative" data-testid="combo-overprice-warning">
            <TriangleAlert/> ₹{Math.abs(savings)} over — this combo costs more than its parts
          </i>
        )}
        <small>Preview from the menu prices on this terminal. The server recalculates both when you save.</small>
      </div>

      <label>Find an item<Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Name or short code" data-testid="combo-item-search-input"/></label>
      <label>Menu category
        <select value={category} onChange={e => setCategory(e.target.value)} data-testid="combo-item-category-select">
          {tabs.map(name => <option value={name} key={name}>{name}</option>)}
        </select>
      </label>

      <div className="combo-results" data-testid="combo-item-results">
        {results.map(item => (
          <div className="combo-pick-row" key={item.id} data-testid={`combo-pick-${item.id}`}>
            <span>{item.name} <small>{item.code}</small></span>
            <b>₹{item.price}</b>
            <button onClick={() => add(item)} aria-label={`Add ${item.name}`} data-testid={`combo-pick-${item.id}-add-button`}><Plus/></button>
          </div>
        ))}
        {!results.length && (
          <p className="chart-empty" data-testid="combo-item-results-empty">
            {pickable.length
              ? "No menu item matches that search."
              : "No menu has arrived from this server yet, so there is nothing to put in a combo."}
          </p>
        )}
      </div>

      <Button variant="action" onClick={submit} disabled={saving} data-testid="save-combo-button">
        {editing ? "Replace components" : "Create combo"}
      </Button>
    </div>
  </Modal>;
}
